import React, { useState, useMemo } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Modal, Button, Input, Select } from "./ui";
import { formatMoney } from "../lib/utils";

const todayISO = () => new Date().toISOString().slice(0, 10);
const num = (v) => (v === "" || v == null ? 0 : Number(v));
const POS = "#10B981";
const NEG = "#EF4444";

function Field({ label, optional, children }) {
  const { t } = useApp();
  return (
    <div>
      <label className="text-xs font-medium text-text-secondary block mb-1">
        {label}{optional && <span className="text-text-secondary/60"> {t("optional")}</span>}
      </label>
      {children}
    </div>
  );
}

export default function SellAssetModal({ asset, items, accounts, onClose, onSold }) {
  const { t, toast } = useApp();
  const cur = (asset.currency || "EUR").toUpperCase();
  const isMetal = asset.asset_type === "precious_metal" && asset.metal;
  const held = Number(asset.quantity || 0);
  const linked = useMemo(
    () => (asset.linked_asset_id ? (items || []).find((x) => x.id === asset.linked_asset_id && x.kind === "liability") : null),
    [asset, items]);
  const payoff = linked ? num(linked.value) : 0;

  const [salePrice, setSalePrice] = useState(isMetal ? (asset.live_price ?? "") : (asset.value ?? ""));
  const [sellQty, setSellQty] = useState(isMetal ? held : null);
  const [saleDate, setSaleDate] = useState(todayISO());
  const [fee, setFee] = useState("");
  const [depositAcc, setDepositAcc] = useState("");
  const [busy, setBusy] = useState(false);

  // cost basis per unit (metals: cost/oz)
  const costPerOz = (isMetal && asset.cost != null && held > 0) ? Number(asset.cost) / held : null;
  const qty = isMetal ? Math.min(num(sellQty), held) : 1;
  const proceeds = isMetal ? num(salePrice) * qty : num(salePrice);
  const cost = isMetal
    ? (costPerOz != null ? costPerOz * qty : null)
    : (asset.cost != null && asset.cost !== "" ? Number(asset.cost) : null);
  const capitalGain = cost != null ? proceeds - cost - num(fee) : null;
  const netCash = proceeds - num(fee) - payoff;
  const partial = isMetal && qty > 0 && qty < held - 1e-9;
  const money = (v) => formatMoney(v, cur, { decimals: 0 });
  const money2 = (v) => formatMoney(v, cur, { decimals: 2 });

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/portfolio/assets/${asset.id}/sell`, {
        sale_price: num(salePrice), sale_date: saleDate, fee: num(fee),
        quantity: isMetal ? qty : null,
        deposit_to_account_id: depositAcc || null,
      });
      toast(t("assets_sold_toast"));
      onSold?.(data);
    } catch (e) {
      toast(e?.response?.data?.detail || t("assets_sale_error"));
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${t("assets_sell")} — ${asset.name}`}
      footer={<>
        <Button variant="outline" onClick={onClose} data-testid="sell-cancel">{t("cancel")}</Button>
        <Button onClick={submit} disabled={busy || !salePrice || (isMetal && qty <= 0)} data-testid="sell-confirm">
          {busy ? t("loading") : t("assets_confirm_sale")}
        </Button>
      </>}>
      <div className="space-y-4">
        {isMetal && (
          <Field label={`${t("assets_sell_quantity")} (${t("assets_held")} ${held} oz)`}>
            <div className="flex items-center gap-2">
              <Input type="number" value={sellQty} max={held} onChange={(e) => setSellQty(e.target.value)} placeholder="0.00" data-testid="sell-quantity-input" />
              <Button variant="outline" onClick={() => setSellQty(held)} data-testid="sell-quantity-all">{t("assets_all")}</Button>
            </div>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label={isMetal ? `${t("assets_price_per_oz")} (${cur})` : `${t("assets_sale_price")} (${cur})`}>
            <Input type="number" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} placeholder="0.00" data-testid="sell-price-input" />
          </Field>
          <Field label={t("assets_sale_date")}>
            <Input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} data-testid="sell-date-input" />
          </Field>
        </div>
        <Field label={t("assets_sale_fees")} optional>
          <Input type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0.00" data-testid="sell-fee-input" />
        </Field>

        {/* deposit proceeds into an account */}
        <Field label={t("assets_deposit_proceeds")} optional>
          <Select value={depositAcc} onChange={(e) => setDepositAcc(e.target.value)} data-testid="sell-deposit-account">
            <option value="">{t("assets_no_deposit")}</option>
            {(accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
          </Select>
        </Field>

        {/* live preview */}
        <div className="rounded-lg border p-3 space-y-1.5 text-sm" data-testid="sell-preview">
          {isMetal && (
            <div className="flex justify-between">
              <span className="text-text-secondary">{qty} oz × {money2(num(salePrice))}</span>
              <span className="tabular-nums" data-testid="sell-proceeds">{money(proceeds)}</span>
            </div>
          )}
          {!isMetal && (
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("assets_sale_price")}</span>
              <span className="tabular-nums">{money(proceeds)}</span>
            </div>
          )}
          {num(fee) > 0 && (
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("assets_sale_fees")}</span>
              <span className="tabular-nums" style={{ color: NEG }}>−{money(num(fee))}</span>
            </div>
          )}
          {linked && (
            <div className="flex justify-between">
              <span className="text-text-secondary">{t("assets_mortgage_payoff")} · {linked.name}</span>
              <span className="tabular-nums" style={{ color: NEG }}>−{money(payoff)}</span>
            </div>
          )}
          <div className="flex justify-between pt-1.5 mt-1.5 border-t font-semibold">
            <span data-testid="sell-net-cash-label">{t("assets_net_cash")}</span>
            <span className="tabular-nums" data-testid="sell-net-cash" style={{ color: netCash >= 0 ? POS : NEG }}>
              {netCash >= 0 ? "+" : "−"}{money(Math.abs(netCash))}
            </span>
          </div>
          {capitalGain != null && (
            <div className="flex justify-between text-xs">
              <span className="text-text-secondary">{t("assets_capital_gain")}</span>
              <span className="tabular-nums" data-testid="sell-capital-gain" style={{ color: capitalGain >= 0 ? POS : NEG }}>
                {capitalGain >= 0 ? "+" : "−"}{money(Math.abs(capitalGain))}
              </span>
            </div>
          )}
        </div>
        {partial && <p className="text-[11px] text-text-secondary" data-testid="sell-partial-note">{t("assets_partial_note").replace("{n}", (held - qty).toFixed(2))}</p>}
        {linked && <p className="text-[11px] text-text-secondary">{t("assets_mortgage_close_note")}</p>}
      </div>
    </Modal>
  );
}
